import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Skeleton, Text } from '@chakra-ui/react';
import {
  AcademicYearOption,
  LeaseTermDateBased,
  OccupancyType,
  SystemTranslationKeys,
} from '@common/entities';
import { useQuery } from '@tanstack/react-query';
import { RootState } from 'state/store';

import { useSystemTranslation } from 'hooks';
import { useLocale } from 'hooks/utils/useLocale';
import { getAcademicYears, getDateBasedLeaseTerms } from 'services';
import { LeaseTerm } from 'types/floorplan-page-types';
import { parseDateString } from 'utils/date';
import { formatCurrency, formatNumber } from 'utils/format';

interface Props {
  occupancyType: OccupancyType;
  leaseTerm: LeaseTerm;
  isSUS: boolean;
}
const SelectedLeaseTerms = ({ occupancyType, leaseTerm, isSUS }: Props) => {
  const { tSystem } = useSystemTranslation();

  if (occupancyType === OccupancyType.Conventional || occupancyType === OccupancyType.Affordable) {
    const {
      amount,
      rateFrequency,
      termMonth,
      id: leaseId,
    } = leaseTerm || { amount: 0, rateFrequency: '', termMonth: '', id: '' };

    return (
      <Text fontSize="xs" mt={1} key={`lease-info-${leaseId}`}>
        {tSystem(SystemTranslationKeys.cart.leaseTermSummaryWithFrequency, {
          term: formatNumber(termMonth),
          amount: formatCurrency(amount ?? 0),
          frequency: rateFrequency || tSystem(SystemTranslationKeys.common.perMonth),
        })}
      </Text>
    );
  }

  if (occupancyType === OccupancyType.Student) {
    return isSUS ? (
      <SUSViewLeaseTerm lease={leaseTerm} />
    ) : (
      <NonSUSViewLeaseTerm lease={leaseTerm} />
    );
  }
  return null;
};

export default SelectedLeaseTerms;

interface StudentLeaseTermProps {
  lease: LeaseTerm;
}
const SUSViewLeaseTerm = ({ lease }: StudentLeaseTermProps) => {
  const { propertyId, orgId } = useParams();
  const orgIdNum = Number(orgId);
  const propertyIdNum = Number(propertyId);

  const { data, isLoading, error } = useQuery<AcademicYearOption[]>({
    queryKey: ['academic-years', propertyId, orgIdNum],
    queryFn: async () => {
      if (typeof getAcademicYears !== 'function') return [];
      return getAcademicYears(propertyIdNum, orgIdNum);
    },
    enabled: !!lease,
    retry: false,
  });

  if (isLoading) return <Skeleton height="10px" />;
  if (error) return <Text>-</Text>;
  if (!Array.isArray(data)) return '';
  const selectedLease = data.find(
    (i) =>
      Number(i.leaseTermId) === Number(lease.id) &&
      Number(i.leaseStartWindowId) === Number(lease.leaseStartWindowId),
  );
  if (!selectedLease) return '';
  return <Text fontSize="xs">{selectedLease.name}</Text>;
};

const NonSUSViewLeaseTerm = ({ lease }: StudentLeaseTermProps) => {
  const locale = useLocale();
  const { propertyId, orgId } = useParams();
  const orgIdNum = Number(orgId);
  const propertyIdNum = Number(propertyId);

  const { applicationId } = useSelector(
    (state: RootState) => state.application.applicant?.currentResponse,
  );

  const { data, isLoading, error } = useQuery<LeaseTermDateBased[]>({
    queryKey: ['lease-terms', propertyId, applicationId, orgIdNum],
    queryFn: () => getDateBasedLeaseTerms(propertyIdNum, applicationId, orgIdNum),
    enabled: !!lease,
    retry: false,
  });

  if (isLoading) return <Skeleton height="10px" />;
  if (error) return <Text>-</Text>;
  if (!Array.isArray(data)) return '';
  const selectedLease = data.find(
    (i) =>
      Number(i.id) === Number(lease.id) &&
      Number(i.leaseStartWindowId) === Number(lease.leaseStartWindowId),
  );

  if (!selectedLease) return '';
  if (!selectedLease.startDate || !selectedLease.endDate) {
    return <Text fontSize="xs">{selectedLease.name}</Text>;
  }

  const startDate = parseDateString(selectedLease.startDate);
  const endDate = parseDateString(selectedLease.endDate);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return <Text fontSize="xs">{selectedLease.name}</Text>;
  }

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const str = `${selectedLease.name} (${dateFormatter.format(startDate)} - ${dateFormatter.format(
    endDate,
  )})`;
  return <Text fontSize="xs">{str}</Text>;
};
